import { Component, Input, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { cloneDeep, map, subtract } from "lodash";
import { filter } from "lodash";
import { sortBy } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AppService } from "src/app/apps/app.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";

@Component({
  selector: "lhpe-audience-container",
  templateUrl: "./audience-container.component.html",
  styleUrls: ["./audience-container.component.scss"],
})
export class AudienceContainerComponent implements OnInit {
  audienceForm!: FormGroup;
  registrationStatusOptions!: { name: string; value: string }[];
  physicalPresenceOptions!: { name: string; value: string }[];
  locationPermissionOptions!: { name: string; value: string }[];
  notificationPermissionOptions!: { name: string; value: string }[];
  blutetoothPermissionOptions!: { name: string; value: string }[];
  isNewAudience!: boolean;
  behavioralTraits!: any[];
  locationOptions!: any[];
  defaultTraits!: { id: string; name: string; type: string }[];
  selectedBehaviorTraits!: any[];
  behavioralTraitsOptions!: any[];
  traitForms!: any;
  conditionals!: {
    [key: string]: { value: string; name: string }[];
  };
  audienceCount: any;
  audience: any;
  @Input() fromCustomer!: boolean;
  @Input() keyword!: string;
  hasQsWrite: boolean = false;
  hasBrList: any;
  hasGfList: any;
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private SessionService: SessionService,
    private AppService: AppService,
    public uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.audience = this.activatedRoute.snapshot.data.audience;

    this.isNewAudience = !this.audience;

    if(this.audience){
      this.keyword = this.audience.keyword;
    }

    const geofences = this.activatedRoute.snapshot.data.geofences.records.map(
      (geofence: any) => {
        return {
          ...geofence,
          type: "geofence",
        };
      },
    );
    const beaconRegions =
      this.activatedRoute.snapshot.data.beaconRegions.records.map(
        (geofence: any) => {
          return {
            ...geofence,
            type: "beacon-region",
          };
        },
      );

    this.hasBrList = this.activatedRoute.snapshot.data.permissions.hasBrList;
    this.hasGfList = this.activatedRoute.snapshot.data.permissions.hasGfList;
    this.hasQsWrite = this.activatedRoute.snapshot.data.permissions.hasQsWrite;

    if (this.hasBrList && this.hasGfList) {
      this.locationOptions = [...geofences, ...beaconRegions].sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
    }

    const audienceNameValidators = this.fromCustomer
      ? []
      : [Validators.required];

    this.audienceForm = new FormGroup({
      audienceName: new FormControl(
        this.audience?.name,
        audienceNameValidators,
      ),
      keyword: new FormControl(
        this.audience?.keyword
      ),
      registrationStatus: new FormControl(
        this.audience?.registration_state ?? "all",
      ),
      physicalPresence: new FormControl(
        this.audience?.on_property_state ?? "all",
      ),
      locationPermission: new FormControl(
        this.audience?.location_permission_state ?? "all",
      ),
      notificationPermission: new FormControl(
        this.audience?.notification_permission_state ?? "all",
      ),
      bluetoothPermission: new FormControl(
        this.audience?.btle_permission_state ?? "all",
      ),
      currentLocations: new FormControl(this.audience?.locations ?? []),
      addTrait: new FormControl(""),
    });

    this.initTraits();

    this.initChangesObservable();
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get currentApp() {
    return this.SessionService.currentAppValue!;
  }

  get facilities() {
    return this.SessionService.facilitiesValue!;
  }

  get selectedTraitsDTO() {
    return this.selectedBehaviorTraits.map((trait) => {
      let _trait = cloneDeep(trait);
      let answer = "";

      if (_trait.type === "Date") {
        const dateObj = this.traitForms[_trait.id].controls.answer.value;
        const dayNumber = dateObj?.getDate();
        const isSingleDigitDay = dayNumber < 10;
        const day = `${isSingleDigitDay ? "0" : ""}${dayNumber.toString()}`;

        const monthNumber = dateObj?.getMonth() + 1;
        const isSingleDigitMonth = monthNumber < 10;
        const month = `${
          isSingleDigitMonth ? "0" : ""
        }${monthNumber.toString()}`;

        const yearNumber = dateObj?.getFullYear();
        const year = yearNumber.toString().slice(2);

        answer = `${month}/${day}/${yearNumber}`;
      } else {
        answer = this.traitForms[_trait.id].controls.answer.value;
      }

      _trait.options = undefined;

      // Ensure server-expected profile field path is present when missing
      if (!_trait.field || (typeof _trait.field === "string" && _trait.field.trim().length === 0)) {
        _trait.field = `appUser.profile.${_trait.id}`;
      }

      return {
        ..._trait,
        _type: _trait.type,
        operator: this.traitForms[_trait.id].controls.conditional.value,
        value: answer,
      };
    });
  }

  get audienceDTO() {

    const selected = this.selectedTraitsDTO;

    const dto = {
      account_app_id: this.currentApp.id,
      btle_permission_state:
        this.audienceForm.controls.bluetoothPermission.value,
      location_permission_state:
        this.audienceForm.controls.locationPermission.value,
      notification_permission_state:
        this.audienceForm.controls.notificationPermission.value,
      on_property_state: this.audienceForm.controls.physicalPresence.value,
      registration_state: this.audienceForm.controls.registrationStatus.value,
      keyword: this.audienceForm.controls.keyword.value,
      name: this.audienceForm.controls.audienceName.value,
      selected,
      locations: this.audienceForm.controls.currentLocations.value.map(
        (location: any) => {
          return `${location.type}:${location.id}`;
        },
      ),
    };

    const selectedDTO: any = {};
    for (let selectedItem of selected) {
      const selectedKey = `profile_${selectedItem.id}`;
      const selectedValue = `${selectedItem.field}|${selectedItem.type}|${selectedItem.operator}|${selectedItem.value}`;
      selectedDTO[selectedKey] = selectedValue;
    }

    return { ...dto, ...selectedDTO };
  }

  get filterDTO() {
    const audienceDTO = this.audienceDTO;
    audienceDTO.name = audienceDTO.filter_name;
    return audienceDTO;
  }

  get filterObj() {
    return {
      facility_id: this.facilities.map((facility) => facility.id),
      filter: this.filterDTO,
    };
  }

  initTraits() {
    this.initConditionals();

    this.traitForms = {};

    this.audienceForm.controls.addTrait.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((trait) => {
        this.initSelectedTrait({ ...trait });

        setTimeout(() => {
          this.audienceForm.controls.addTrait.setValue(null, {
            emitEvent: false,
          });
        });
      });

    const defaultTraits = [
      {
        id: "last_name",
        name: "Last Name",
        type: "String",
        field: "last_name",
      },
      {
        id: "first_name",
        name: "First Name",
        type: "String",
        field: "first_name",
      },
      {
        id: "company",
        name: "Company",
        type: "String",
        field: "company",
      },
      {
        id: "gender",
        name: "Gender",
        type: "String",
        field: "gender",
      },
      {
        id: "date_of_birth",
        name: "Date of Birth",
        type: "Date",
        field: "date_of_birth",
        cellFilter: "date:'short'",
      },
      {
        id: "email",
        name: "Email",
        type: "String",
        field: "email",
      },
      {
        id: "on_property",
        name: "On Property",
        type: "Boolean",
        field: "on_property",
        cellFilter: "yesNo",
      },
      {
        id: "created",
        name: "Created",
        type: "Date",
        field: "created_at",
        cellFilter: "date:'short'",
      },
      {
        id: "location",
        name: "Location",
        type: "String",
        field: "locations",
      },
      {
        id: "location_history",
        name: "Location History",
        type: "LocationHistory",
        field: "location_history",
      },
    ];

    const profileTraits = this.currentAccount?.catalog?.app_users?.profile;
    const traits = Array.isArray(profileTraits)
      ? [
          ...profileTraits.map((trait: any) => {
            return { ...trait, type: trait._type };
          }),
        ]
      : defaultTraits;

    const hiddenTraits = [
      "last_name",
      "first_name",
      "on_property",
      "location",
      "image",
      "created",
      "location_history",
    ];

    this.behavioralTraits = traits.filter((trait) => {
      return (
        !hiddenTraits.includes(trait.id) &&
        (trait.customers === undefined || trait.customers)
      );
    });

    this.selectedBehaviorTraits = [];

    //
    this.behavioralTraitsOptions = this.initTraitsOptions(this.behavioralTraits);

    //Init traits and locations of an already created broadcast
    if (this.audience?.selected) {
      for (let trait of this.audience.selected) {
        this.initSelectedTrait(trait);
        this.traitForms[trait.id].controls.conditional.setValue(trait.operator);
        if (trait.type === "Date") {
          this.traitForms[trait.id].controls.answer.setValue(
            new Date(trait.value),
            { emitEvent: false },
          );
        } else {
          this.traitForms[trait.id].controls.answer.setValue(trait.value, {
            emitEvent: false,
          });
        }
      }
    }
    if (this.audience?.locations) {
      let locArr = [];
      for (let location of this.audience?.locations) {
        let [type, id] = location.split(":");

        const locationObj = this.locationOptions?.find((option) => {
          return option.id === Number.parseInt(id) && option.type === type;
        });

        if (locationObj) {
          locArr.push(locationObj);
        }
      }
      this.audienceForm.controls.currentLocations.setValue(locArr, {
        emitEvent: false,
      });
    }
  }

  initTraitsOptions(traits: any) : any {
    let ret : any[] = [];
    for (let trait of traits) {
      let results = this.extractTraits(trait, []);
      results.forEach((result: any) => {
        ret.push(result);
      });
    }
    //console.log('initTraitsOptions result', ret)
    return ret;
  }

  extractTraits(trait: any, results: any) : any {
    let t = cloneDeep(trait);
    if(t.type === "Object" || t.type === "Object[]"){
      for(let subTrait of t.fields){
        subTrait.id = t.id + "." + subTrait.id;
        subTrait.field = t.field + (t.type == "Object[]" ? "[]" : "") + "." + subTrait.field;
        subTrait.name = t.name + " -> " + subTrait.name;
        subTrait.type = subTrait._type;
        let subTraits = this.extractTraits(subTrait, results);
        results = subTraits;
      }
    }
    else {
      results.push(t);
    }
    return results;
  }

  initSelectedTrait(trait: any) {

    //console.log('initSelectedTrait', trait)

    if(trait.type === "Boolean"){
      trait.options = [
        {
          label: "Yes",
          value: "true",
        },
        {
          label: "No",
          value: "false",
        },
      ];
    }

    if(trait.ref){
      switch(trait.ref.type){
        case "facility":
          trait.options = map(sortBy(this.facilities, 'name'), (facility: any) => {
            return {
              label: facility.name,
              value: facility.foreign_id,
            };
          });
        break;
      }
    }else if(trait.options && trait.sortBy !== undefined && trait.sortBy !== "label"){
      trait.options = sortBy(trait.options, trait.sortBy)
    }else{
      trait.options = sortBy(trait.options, 'label')
    }

    trait.uuid = trait.id + "_" + Math.random().toString(16).slice(2, 8);

    this.selectedBehaviorTraits.push(trait);

    // Ensure server-expected profile field path is present on the trait
    if (!trait.field || (typeof trait.field === "string" && trait.field.trim().length === 0)) {
      trait.field = `appUser.profile.${trait.id}`;
    }

    this.traitForms[trait.id] = new FormGroup({
      name: new FormControl(trait.name),
      conditional: new FormControl("", [Validators.required]),
      answer: new FormControl("", [Validators.required]),
    });

    this.traitForms[trait.id].controls.name.disable();

    if(this.conditionsForTrait(trait).length === 1){
      this.traitForms[trait.id].controls.conditional.setValue(this.conditionsForTrait(trait)[0].value)
      this.traitForms[trait.id].controls.conditional.disable();
    }

    //Subscribe to value changes on new traitForm
    this.traitForms[trait.id].valueChanges.subscribe((change: any) => {
      if (this.traitForms[trait.id].valid) {
        this.getCount();
      }
    });

    // this.behavioralTraitsOptions = this.behavioralTraitsOptions.filter(
    //   (traitOption) => {
    //     return traitOption.id !== trait.id;
    //   },
    // );
  }

  initConditionals() {
    this.conditionals = this.uiService.conditionals;
  }

  initChangesObservable() {
    // this.getCount();
    this.audienceForm.controls.keyword.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.getCount();
      });
    this.audienceForm.controls.registrationStatus.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.getCount();
      });
    this.audienceForm.controls.physicalPresence.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.getCount();
      });
    this.audienceForm.controls.locationPermission.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.getCount();
      });
    this.audienceForm.controls.notificationPermission.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.getCount();
      });
    this.audienceForm.controls.bluetoothPermission.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.getCount();
      });
    this.audienceForm.controls.currentLocations.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.getCount();
      });
  }

  removeBehaviorTrait(removedBehavior: any) {
    delete this.traitForms[removedBehavior.id];

    this.selectedBehaviorTraits = this.selectedBehaviorTraits.filter(
      (trait) => {
        return trait.uuid !== removedBehavior.uuid;
      },
    );

    this.getCount();
  }

  getCount() {
    this.AppService.getFilterCount(
      this.currentAccount.accountID,
      this.currentApp.id,
      this.filterObj,
    )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res: any) => {
          this.audienceCount = res.count;
        },
        (err) => {
          this.audienceCount = "n/a";
        },
      );
  }

  removeLocation(location: any) {
    const filteredLocations =
      this.audienceForm.controls.currentLocations.value.filter(
        (selectedLocation: any) => selectedLocation !== location,
      );
    this.audienceForm.controls.currentLocations.setValue(filteredLocations);
  }

  traits(){

    let ret = this.behavioralTraitsOptions

    // filter out traits that are already selected
    for(let selected of this.selectedBehaviorTraits){
      ret = ret.filter((trait: any) => {
        return trait.id !== selected.id;
      });
    }

    // sort by name
    return ret.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }

  conditionsForTrait(trait: any) {
    let ret = this.conditionals[trait.type];
    if(trait.type.indexOf('[]') == -1 && trait.ref && trait.ref.type === "facility"){
      ret = filter(this.conditionals[trait.type], (cond: any) => {
        return ["eq","neq"].indexOf(cond.value) > -1;
      });
    }
    //console.log('conditionsForTrait', trait, ret)
    return ret;
  }

  debug(data: any) {
    console.log(data);
    return typeof data;
  }
}
